import { useState } from 'react'
import Title from '../../common/Title'
import { useIntl } from 'react-intl'
import Card from '../../common/Card'
import FileUploadButton from '../../common/FileUploadButton'
import { Select } from 'ui/fields'
import { useGetProgramFilter } from '../../../hooks/useGetProgramFilter'
import { Button } from 'ui'
import { faFileArrowDown, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation } from '@tanstack/react-query'
import { getOperationLayoutPMS, importFilePMSOperations } from '../../../api/operation'
import useIsProcessingImport from '../../../hooks/useIsProcessingImport/useIsProcessingImport'
import Alert from 'ui/Alert'
import { getAlertType } from '../../../utils/partnerUtils'
import { createDateFromISO } from 'utils/dates'
import useGetInstructionTableOperationPMS from '../../../hooks/useGetInstructionTableOperationPMS'
import { toast } from 'react-toastify'
import classNames from 'classnames'

function ImportOperation() {
   const { data: programs, isLoading } = useGetProgramFilter()
   const { data: dataImported, reset, isDone } = useIsProcessingImport({ type: 'OPERATIONS_PMS' })

   const intl = useIntl()
   const [error, setError] = useState(null)
   const [file, setFile] = useState(null)
   const [isExporting, setIsExporting] = useState(false)

   const [isLoadingImport, setIsLoadingImport] = useState(false)
   const [programId, setProgramId] = useState('')
   const [showInfo, setShowInfo] = useState(false)
   const { mutate: submitForm } = useMutation({
      mutationFn: importFilePMSOperations,
   })
   const { mutate: getLayout } = useMutation({
      mutationFn: getOperationLayoutPMS,
   })
   const { data: table, isLoading: tableInstruction } = useGetInstructionTableOperationPMS(programId)
   const onSubmit = () => {
      if (!programId || !file) {
         return
      }
      setIsLoadingImport(true)
      submitForm(
         {
            loyaltyPlanId: Number(programId),
            file: file,
         },
         {
            onSuccess: () => {
               setIsLoadingImport(false)
               setProgramId('')
               setFile(null)
            },
            onError: (e: any) => {
               setIsLoadingImport(false)
               setError(e.message)
               setFile(null)
            },
         },
      )
   }
   const handleLayoutButton = () => {
      getLayout(
         {},
         {
            onSuccess: (response) => {
               const blob = new Blob([response], {
                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
               })
               const url = window.URL.createObjectURL(blob)

               const link = document.createElement('a')
               link.href = url
               link.download = 'import_pms_layout.xlsx'
               document.body.appendChild(link)
               link.click()
               document.body.removeChild(link)
               window.URL.revokeObjectURL(url)
               link.remove()
               setIsExporting(false)
               toast.success(intl.formatMessage({ id: 'Archivo descargado correctamente' }))
            },
            onError: (e: any) => {
               setIsExporting(false)
               toast.error('Error al exportar los datos')
            },
         },
      )
   }
    if (dataImported && dataImported.result && !isDone) {
      const { status } = dataImported.result
      const isInProgress = status === 'IN_PROGRESS'
      return (
         <div className="px-5 py-2">
            <Card className="p-5">
               <Alert className="m-5" type={getAlertType(status)}>
                  <Alert.Body className="font-semibold flex items-center gap-3">
                     <svg
                        className="animate-spin h-5 w-5 text-blue-500 shrink-0"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                     >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                     </svg>
                     <div>
                        <div>{isInProgress ? 'Procesando importación...' : 'Importación en cola, espere...'}</div>
                        <div className="text-sm font-normal text-gray-500 mt-1">
                           Este proceso puede tardar unos momentos. No cierre esta ventana.
                        </div>
                     </div>
                  </Alert.Body>
               </Alert>
            </Card>
         </div>
      )
   }
   if (dataImported && dataImported.result && isDone) {
      const { result, message } = dataImported
      const { importDetails, processed, notProcessed, status, createdAt } = result
      const hasImportErrors = importDetails && importDetails.length > 0
      return (
         <div className="px-5 py-2">
            <Card className="p-5">
               <Alert className={'m-5'} type={getAlertType(status)}>
                  <Alert.Body className="font-semibold">
                     <div>{message}</div>
                     <div>
                        {intl.formatMessage(
                           { id: 'partner.createAt' },
                           {
                              date: createDateFromISO(createdAt)
                                 .setLocale(intl.locale)
                                 .toFormat("cccc d 'de' LLLL 'del' yyyy"),
                           },
                        )}
                     </div>
                    
                  </Alert.Body>
               </Alert>
                <div className="mx-5 mb-4 flex justify-between items-center">
                  {processed !== null && notProcessed !== null && (
                        <div className="mt-2 flex gap-4 text-sm font-normal">
                           <span className="text-green-700">✓ Procesados: {processed}</span>
                           {notProcessed > 0 && (
                              <span className="text-red-600">✗ No procesados: {notProcessed}</span>
                           )}
                        </div>
                     )}
                  <Button onClick={reset} bsStyle="primary">
                     Nueva importación
                  </Button>
               </div>
                 {hasImportErrors && (
                  <div className="mx-5 mb-5">
                     <h3 className="font-semibold text-gray-700 mb-3">
                        Detalle de registros no procesados ({importDetails.length})
                     </h3>
                     <div className="overflow-x-auto rounded border border-gray-200">
                        <table className="w-full text-sm">
                           <thead className="bg-gray-50">
                              <tr>
                                 <th className="px-4 py-3 border-b text-left font-medium text-gray-600">ID</th>
                                 <th className="px-4 py-3 border-b text-left font-medium text-gray-600">External ID</th>
                                 <th className="px-4 py-3 border-b text-left font-medium text-gray-600">Nombre</th>
                                 <th className="px-4 py-3 border-b text-left font-medium text-gray-600">Email</th>
                                 <th className="px-4 py-3 border-b text-left font-medium text-gray-600">Motivo</th>
                              </tr>
                           </thead>
                           <tbody className="divide-y divide-gray-100">
                              {importDetails.map((detail, index) => (
                                 <tr key={detail.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-4 py-3">{detail.id}</td>
                                    <td className="px-4 py-3">{detail.externalId}</td>
                                    <td className="px-4 py-3">
                                       {detail.firstName || detail.lastName
                                          ? `${detail.firstName ?? ''} ${detail.lastName ?? ''}`.trim()
                                          : <span className="text-gray-400 italic">—</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                       {detail.email ?? <span className="text-gray-400 italic">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-red-600">{detail.message}</td>
                                 </tr>
                              ))}
                           </tbody>
                        </table>
                     </div>
                  </div>
               )}
            </Card>
         </div>
      )
   }
   return (
      <>
         <Title
            className="flex justify-between gap-x-2  items-center "
            value={intl.formatMessage({ id: 'operations.import' })}
         ></Title>
         <div className="px-5 py-2">
            <Card className="p-5">
               <FileUploadButton
                  loading={isLoadingImport}
                  disabled={isLoadingImport || isLoading || tableInstruction}
                  file={file}
                  setFile={setFile}
                  error={error}
                  setError={setError}
                  helpText={intl.formatMessage({ id: 'operations.importHelpText' })}
                  fileTypeList={['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']}
                  loadingLabel="Cargando operaciones..."
               />
               <div className="border border-t-0 border-gray-300 hover:border-gray-400 flex items-end justify-between">
                  <div className="p-4">
                     <Select
                        label={intl.formatMessage({ id: 'program.planName' })}
                        value={programId}
                        onChange={setProgramId}
                        showErrorText={true}
                        disabled={isLoadingImport || isLoading || tableInstruction}
                     >
                        <Select.Option value="">--</Select.Option>
                        {programs?.items.map((program) => (
                           <Select.Option key={program.id} value={program.id}>
                              {program.name}
                           </Select.Option>
                        ))}
                     </Select>
                  </div>
                  <div className="p-4">
                     <Button
                        onClick={onSubmit}
                        disabled={!programId || !file || isLoadingImport || isLoading}
                        buttonStyle={{ paddingLeft: '35px', paddingRight: '35px' }}
                        bsStyle="primary"
                     >
                        Continuar
                     </Button>
                  </div>
               </div>
               <div className="mt-10">
                  <div className="flex justify-between mb-7">
                     <div className="flex items-center ">
                        <button
                           className={classNames('font-semibold cursor-pointer hover:underline', {
                              'text-gray-500':
                                 isLoadingImport || isLoading || isExporting || !programId || tableInstruction,
                           })}
                           onClick={() => setShowInfo(!showInfo)}
                           disabled={isLoadingImport || isLoading || isExporting || !programId || tableInstruction}
                        >
                           Mostrar instrucciones de llenado
                        </button>
                        {showInfo ? (
                           <FontAwesomeIcon
                              icon={faSortUp}
                              className={classNames('ml-1 text-gray-500', {
                                 'text-gray-500':
                                    isLoadingImport || isLoading || isExporting || !programId || tableInstruction,
                              })}
                           />
                        ) : (
                           <FontAwesomeIcon
                              icon={faSortDown}
                              className={classNames('ml-1 text-gray-500', {
                                 'text-gray-500':
                                    isLoadingImport || isLoading || isExporting || !programId || tableInstruction,
                              })}
                           />
                        )}
                     </div>
                     {showInfo && (
                        <Button
                           onClick={handleLayoutButton}
                           disabled={isLoadingImport || isLoading || isExporting || !programId || tableInstruction}
                           buttonStyle={{ paddingLeft: '35px', paddingRight: '35px' }}
                           bsStyle="outline-primary"
                        >
                           <FontAwesomeIcon icon={faFileArrowDown} className="mr-1 text-gray-500" />
                           Descargar archivos de ejemplo
                        </Button>
                     )}
                  </div>
                  {showInfo && programId && (
                     <div className="overflow-x-auto">
                        <table className="w-full text-sm border">
                           <thead className="bg-gray-50">
                              <tr>
                                 <th className="px-4 py-3 border text-left font-medium text-gray-600">DATO</th>
                                 <th className="px-4 py-3 border text-left font-medium text-gray-600">COLUMNA</th>
                                 <th className="px-4 py-3 border text-left font-medium text-gray-600">TIPO DE DATO</th>
                                 <th className="px-4 py-3 border text-left font-medium text-gray-600">OPCIONES</th>
                              </tr>
                           </thead>
                           <tbody className="divide-y divide-gray-200">
                              {table?.map((row, index) => (
                                 <tr key={index} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                    <td className="px-4 py-3">{row.name}</td>
                                    <td className="px-4 py-3">{row.column}</td>
                                    <td className="px-4 py-3">{row.dataType}</td>
                                    <td className="px-4 py-3">{row.values}</td>
                                 </tr>
                              ))}
                           </tbody>
                        </table>
                     </div>
                  )}
               </div>
            </Card>
         </div>
      </>
   )
}

export default ImportOperation
